import React, { useEffect } from 'react';
import { useAppDispatch } from './useAppDispatcher.hook';
import {
  getNotificationsListThunk,
  getNotificationTypesThunk,
} from '../redux/Notification/NotificationThunk';
import { Notification } from '../redux/Notification/NotificationType';
import { SelectOptionProps } from '../components/Input/Select.component';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  NOTIFICATION_ICON_COLOR,
  NotificationTypes,
} from '../constants/notification.constant';
import {
  isDefaultNotification,
  isGrantedNotification,
} from '../services/notification.service';
import { checkFCMAvailable, setUpFCM } from '../services/fcm.service';
import { useModalManager } from './ModalManager.hook';
import TurnOnNotificationModalComponent from '../components/Modal/TurnOnNotificationModal.component';
import { useToast } from './useToast';
import { setUserConfigurationFCMTokenThunk } from '../redux/User/Configuration/UserConfigurationThunk';
import {
  CentrifugoEventType,
  useCentrifugoManager,
} from './CentrifugoManager.hook';
import FlyNotifyBlockComponent from '../components/Blocks/FlyNotifyBlock.component';

const StyledNotificationTypeIndicator = styled.div<{
  color: string;
}>`
  width: 8px;
  height: 8px;
  border-radius: 50%;

  ${({ theme, color }) => `background: ${theme.colors[color]}`};
`;
const StyledNotificationType = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ConnectNotificationToContainer = () => {
  const { t } = useTranslation();
  const { openModal } = useModalManager();
  const dispatch = useAppDispatch();
  const { subscribe, unsubscribe } = useCentrifugoManager();
  const { successToast, errorToast, customToast } = useToast();

  useEffect(() => {
    if (!isDefaultNotification() && !isGrantedNotification()) return;
    checkFCMAvailable().then(() => {
      isDefaultNotification() && openModal(TurnOnNotificationModalComponent);
      isGrantedNotification() &&
        setUpFCM()
          .then(token => {
            dispatch(setUserConfigurationFCMTokenThunk(token));
            successToast(t('notifications.toast.enabled'));
          })
          .catch(err => {
            console.log(err);
            errorToast(t('notifications.toast.error_enabling'));
          });
    });
  }, []);

  useEffect(() => {
    subscribe(
      CentrifugoEventType.NOTIFICATION_EVENT,
      ({ id: uuid, type, notification }: any) => {
        if (type === NotificationTypes.NOTIFICATION_TYPE) {
          customToast(
            ({ id }: any) => (
              <FlyNotifyBlockComponent id={id} uuid={uuid} {...notification} />
            ),
            {
              position: 'top-right',
              duration: 1000 * 60 * 60, // 1 hour
            },
          );
        }
      },
    );

    return () => {
      unsubscribe(CentrifugoEventType.NOTIFICATION_EVENT);
    };
  }, []);

  return <React.Fragment />;
};

const useNotificationHook = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  return {
    getSelectList: async (
      search: string,
      loadedOptions: string | any[],
      { page }: any,
    ) => {
      const {
        payload: { data, pagination },
      } = await dispatch(getNotificationsListThunk({ page }));
      return {
        options: data.map((item: Notification) => ({
          value: item.uuid,
          label: item.type,
        })),
        hasMore: pagination.current_page < pagination.last_page,
        additional: {
          page: pagination.current_page + 1,
        },
      };
    },
    getSelectTypes: async () => {
      const {
        payload: { data },
      } = await dispatch(getNotificationTypesThunk());
      return {
        options: data.map((item: SelectOptionProps) => ({
          ...item,
          label: (
            <React.Fragment>
              <StyledNotificationType>
                <StyledNotificationTypeIndicator
                  color={NOTIFICATION_ICON_COLOR[item.value]}
                />
                <div>{t(`notifications.types.${item.label}`)}</div>
              </StyledNotificationType>
            </React.Fragment>
          ),
        })),
        hasMore: false,
      };
    },
  };
};

export default useNotificationHook;
